import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  SUBSCRIPTION_USAGE_QUERY_KEY,
  WINDOW_LIMIT_QUERY_KEY,
  useSubscriptionUsage,
  useWindowLimit,
} from 'features/usage/hooks/use-usage';
import type { WindowUsage } from 'features/usage/services/usage-service';
import { formatWindowName } from 'features/usage/utils/usage-format';
import { useMyPlan } from 'features/subscription/hooks/use-seats';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { AI_CREDITS_METER, CREDITS_QUERY_KEY } from '../constants/credits';
import { useCreditSummary } from './use-credits';

// Usage is recorded as a reply ends, so it is read every 3 seconds, up to 3 times, until a read
// shows something new.
const READ_INTERVAL_MS = 3000;
const MAX_READS = 3;

interface PlanLimit {
  percent: number;
  start: string;
  end: string;
}

export interface UsageLimitIndicator {
  planName?: string;
  windows: WindowUsage[];
  plan?: PlanLimit;
  topUpCredits?: number;
  /** What the circle and its hover show. `percent` is empty for a top-up, which has no limit. */
  summary: { label: string; percent?: number; resetsAt?: string };
}

/**
 * The one limit the circle by the model picker stands for: today's window, else the plan's
 * month, else the top-up balance. Empty with no plan and no top-up, so nothing is shown.
 */
export const useUsageLimitIndicator = (): UsageLimitIndicator | null => {
  const { t } = useTranslation();
  const userId = useCommonStore((state) => state.user?.id) ?? '';
  const { place, hasPlan, isFreeAccess, isLoading } = useMyPlan();
  const { data: credits } = useCreditSummary(false);
  const { data: windowLimit } = useWindowLimit(userId);
  const { data: meters } = useSubscriptionUsage();

  const windows = windowLimit?.windows ?? [];
  const meter = (meters ?? []).find((m) => m.meterKey === AI_CREDITS_METER && m.included > 0);
  const plan = meter && {
    percent: (meter.used / meter.included) * 100,
    start: meter.periodStartUtc,
    end: meter.periodEndUtc,
  };
  const topUpCredits =
    credits?.enabled && credits.hasAccess !== false && credits.balanceCredits > 0
      ? credits.balanceCredits
      : undefined;
  const daily = windows.find((window) => window.type === 'daily');

  if (isLoading || isFreeAccess || !hasPlan) return null;

  const shared = { planName: place?.planName, windows, plan, topUpCredits };
  if (daily) {
    const label = formatWindowName(daily.name || 'Daily limit', t);
    return { ...shared, summary: { label, percent: daily.percent, resetsAt: daily.resets_at } };
  }
  if (plan) {
    return {
      ...shared,
      summary: {
        label: t('MONTHLY_AI_LIMITS', 'Monthly · AI limits'),
        percent: plan.percent,
        resetsAt: plan.end,
      },
    };
  }
  if (topUpCredits !== undefined) {
    return { ...shared, summary: { label: t('TOP_UP_BALANCE', 'Top-up balance') } };
  }
  return null;
};

/** Reads the limits again after a reply has finished, so the circle and the banner follow every chat. */
export const useRefreshUsageAfterReply = (isBusy: boolean) => {
  const queryClient = useQueryClient();
  const wasBusy = useRef(false);

  useEffect(() => {
    const replyJustEnded = wasBusy.current && !isBusy;
    wasBusy.current = isBusy;
    if (!replyJustEnded) return;

    const isCreditSummary = (query: { queryKey: readonly unknown[] }) =>
      query.queryKey[2] === 'summary';
    const read = () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: [WINDOW_LIMIT_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_USAGE_QUERY_KEY] }),
        queryClient.invalidateQueries({
          queryKey: [CREDITS_QUERY_KEY],
          predicate: isCreditSummary,
        }),
      ]);
    const readNumbers = () =>
      JSON.stringify([
        queryClient.getQueriesData({ queryKey: [WINDOW_LIMIT_QUERY_KEY] }),
        queryClient.getQueriesData({ queryKey: [SUBSCRIPTION_USAGE_QUERY_KEY] }),
        queryClient.getQueriesData({ queryKey: [CREDITS_QUERY_KEY], predicate: isCreditSummary }),
      ]);

    let isCancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const readAndCheck = async (readsDone: number) => {
      const before = readNumbers();
      await read();
      if (isCancelled || readNumbers() !== before || readsDone === MAX_READS) return;
      timer = setTimeout(() => readAndCheck(readsDone + 1), READ_INTERVAL_MS);
    };
    timer = setTimeout(() => readAndCheck(1), READ_INTERVAL_MS);

    return () => {
      isCancelled = true;
      clearTimeout(timer);
    };
  }, [isBusy, queryClient]);
};
